import { useState, KeyboardEvent, ClipboardEvent } from 'react';
import { X } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';

/** Splits pasted/typed text into trimmed, non-empty category names (newline, comma, semicolon). */
export function splitCategories(text: string): string[] {
  return text.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean);
}

/** Appends new categories, ignoring duplicates (case-sensitive, like the backend check). */
export function addCategories(current: string[], text: string): string[] {
  const next = [...current];
  for (const category of splitCategories(text)) {
    if (!next.includes(category)) next.push(category);
  }
  return next;
}

interface CategoryChipInputProps {
  id?: string;
  value: string[];
  onChange: (categories: string[]) => void;
  invalid?: boolean;
  placeholder?: string;
}

/**
 * Chip input for evaluation categories (CA-762): Enter or comma adds, a pasted
 * list (one per line) adds all, Backspace in the empty field removes the last chip.
 */
export function CategoryChipInput({ id, value, onChange, invalid, placeholder }: CategoryChipInputProps) {
  const [draft, setDraft] = useState('');

  const commit = (text: string) => {
    onChange(addCategories(value, text));
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!/[\n,;]/.test(text)) return;
    e.preventDefault();
    commit(text);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1.5 ${invalid ? 'border-red-500' : 'border-gray-200'}`}
    >
      {value.map((category) => (
        <Badge key={category} variant="secondary" className="gap-1">
          {category}
          <button
            type="button"
            aria-label={`${category} entfernen`}
            onClick={() => onChange(value.filter((c) => c !== category))}
            className="rounded hover:text-red-600"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => draft.trim() && commit(draft)}
        placeholder={placeholder ?? 'Kategorie eingeben und Enter drücken …'}
        className="h-7 min-w-[180px] flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
      />
    </div>
  );
}
